import React, { useEffect, useState, useCallback } from 'react';
import { Sun, Moon } from 'lucide-react';
import IconButton from './IconButton';
import { SHADCN, R, TYPO, S } from '../theme';

const STORAGE_KEY = 'sonore-theme';

function readTheme() {
  if (typeof document === 'undefined') return 'dark';
  const fromDom = document.documentElement.getAttribute('data-theme');
  if (fromDom === 'light' || fromDom === 'dark') return fromDom;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch { /* ignore */ }
  return 'dark';
}

function applyTheme(mode) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.classList.add('theme-switching');
  root.setAttribute('data-theme', mode);
  try { localStorage.setItem(STORAGE_KEY, mode); } catch { /* ignore */ }
  window.dispatchEvent(new CustomEvent('sonore:themechange', { detail: { mode } }));
  setTimeout(() => root.classList.remove('theme-switching'), 300);
}

export function useTheme() {
  const [mode, setMode] = useState(readTheme);

  useEffect(() => {
    const onChange = (e) => setMode(e.detail.mode);
    window.addEventListener('sonore:themechange', onChange);
    return () => window.removeEventListener('sonore:themechange', onChange);
  }, []);

  const toggle = useCallback(() => {
    applyTheme(mode === 'dark' ? 'light' : 'dark');
  }, [mode]);

  const set = useCallback((next) => applyTheme(next), []);

  return { mode, toggle, set };
}

// ── Variante icône (TopBar) ─────────────────────────────────────────────────
export default function ThemeToggle({ size = 'md' }) {
  const { mode, toggle } = useTheme();
  const isDark = mode === 'dark';
  return (
    <IconButton
      size={size}
      variant="ghost"
      onClick={toggle}
      aria-label={isDark ? 'Passer en mode clair' : 'Passer en mode sombre'}
      title={isDark ? 'Mode clair' : 'Mode sombre'}
    >
      {isDark ? <Sun size={16} strokeWidth={1.8} /> : <Moon size={16} strokeWidth={1.8} />}
    </IconButton>
  );
}

// ── Variante segmentée (Réglages) ───────────────────────────────────────────
export function ThemeSegmented() {
  const { mode, set } = useTheme();
  const options = [
    { id: 'light', label: 'Clair', Icon: Sun },
    { id: 'dark',  label: 'Sombre', Icon: Moon },
  ];
  return (
    <div
      role="radiogroup"
      aria-label="Thème"
      style={{
        display: 'inline-flex',
        padding: 4,
        gap: 4,
        background: SHADCN.muted,
        border: `1px solid ${SHADCN.border}`,
        borderRadius: R.md,
      }}
    >
      {options.map(opt => {
        const active = mode === opt.id;
        return (
          <button
            key={opt.id}
            role="radio"
            aria-checked={active}
            data-sonore-btn=""
            onClick={() => set(opt.id)}
            style={{
              ...TYPO.cta,
              display: 'inline-flex',
              alignItems: 'center',
              gap: S[2],
              padding: `7px ${S[3]}px`,
              border: 'none',
              borderRadius: R.sm,
              cursor: 'pointer',
              background: active ? SHADCN.cardSolid : 'transparent',
              color: active ? SHADCN.fg : SHADCN.mutedFg,
              boxShadow: active ? SHADCN.shadowSm : 'none',
              transition: `background ${SHADCN.durBase} ${SHADCN.easeOut}, color ${SHADCN.durBase} ${SHADCN.easeOut}`,
            }}
          >
            <opt.Icon size={14} strokeWidth={1.8} />
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
